import { useState, useEffect } from 'react';
import {
  documentsAPI,
  Document,
  DocumentCategory,
  CategoryWithDocuments,
  DocumentStats,
} from '../../lib/api';
import { toast } from 'sonner';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { usePagination, useModal } from '../../hooks';
import { getErrorMessage } from '../../hooks/useFetchData';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { ViewMode, UploadFormData, INITIAL_UPLOAD_FORM, DocumentFilters, INITIAL_FILTERS, DocumentVisibility } from './types';
import { useView } from '../../contexts/ViewContext';

export function useDocuments() {
  const confirm = useConfirm();
  const { viewKey, getViewQueryParams } = useView();
  const [viewMode, setViewMode] = useState<ViewMode>('folder');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState<DocumentStats | null>(null);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<DocumentFilters>(INITIAL_FILTERS);

  // Folder view data
  const [categoriesWithDocs, setCategoriesWithDocs] = useState<CategoryWithDocuments[]>([]);

  // List view data
  const [documents, setDocuments] = useState<Document[]>([]);
  const pagination = usePagination({ pageSize: 20 });
  const uploadDialog = useModal();
  const editDialog = useModal();
  // CR-5330: Document details popup (metadata + audit history + file
  // actions). Mirrors the Delivery / Purchasing detail modals.
  const detailDialog = useModal();
  const [detailDoc, setDetailDoc] = useState<Document | null>(null);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  // CR-5325: Edit form mirrors the upload form — tags, version, three-way
  // visibility (private/shared/public) + share list. We keep document_type
  // and file replacement off the form for now (see EditDialog comment).
  // CR-5329: edit form also exposes the Read & Accept toggle.
  const [editForm, setEditForm] = useState<{
    id: number;
    title: string;
    description: string;
    category: string;
    tags: string;
    version: string;
    visibility: DocumentVisibility;
    shared_with_company_ids: number[];
    requires_acceptance: boolean;
  }>({
    id: 0,
    title: '',
    description: '',
    category: '',
    tags: '',
    version: '',
    visibility: 'private',
    shared_with_company_ids: [],
    requires_acceptance: false,
  });
  const [editSaving, setEditSaving] = useState(false);

  // Upload form state
  const [uploadForm, setUploadForm] = useState<UploadFormData>(INITIAL_UPLOAD_FORM);

  useEffect(() => {
    fetchData();
  }, [viewMode, pagination.currentPage, searchTerm, filters, viewKey]);

  useEffect(() => {
    if (uploadDialog.isOpen || showFilters || editDialog.isOpen) {
      fetchCategories();
    }
  }, [uploadDialog.isOpen, showFilters, editDialog.isOpen]);

  // CR-5328: keep the detail-modal's `document` prop in sync with the
  // refreshed list/folder data. After a successful version upload the
  // parent calls fetchData() to pull new metadata (size, version label),
  // but detailDoc was a stable snapshot so the modal's "Current" row
  // showed the pre-upload version. This effect re-points detailDoc at
  // the freshest copy whenever the list refetches.
  useEffect(() => {
    setDetailDoc(prev => {
      if (!prev) return prev;
      const fresh =
        documents.find(d => d.id === prev.id) ||
        categoriesWithDocs.flatMap(c => c.documents).find(d => d.id === prev.id);
      return fresh && fresh !== prev ? fresh : prev;
    });
  }, [documents, categoriesWithDocs]);

  const fetchCategories = async () => {
    try {
      const response = await documentsAPI.getCategories();
      // Handle both paginated response { results: [...] } and direct array
      const data = response.data as unknown;
      const categoriesData: DocumentCategory[] = Array.isArray(data)
        ? data
        : ((data as { results?: DocumentCategory[] }).results || []);
      setCategories(categoriesData);
    } catch (err: unknown) {
      console.error('Error fetching categories:', err);
      toast.error('Failed to load categories');
    }
  };

  // Helper to apply filters to a document
  const matchesFilters = (doc: Document): boolean => {
    // Document type filter
    if (filters.documentType !== 'all' && doc.document_type !== filters.documentType) {
      return false;
    }
    // Category filter
    if (filters.category !== 'all') {
      const categoryId = parseInt(filters.category, 10);
      if (doc.category !== categoryId) {
        return false;
      }
    }
    // Visibility filter
    // CR-5327: Three visibility states mirror the upload form. A doc is
    // "shared" when it isn't public AND has at least one explicit share
    // (shared_with_company_ids is a non-empty array on the list
    // serializer). Strict private excludes shared docs.
    if (filters.visibility !== 'all') {
      const sharedCount = doc.shared_with_company_ids?.length ?? 0;
      if (filters.visibility === 'public' && !doc.is_public) return false;
      if (filters.visibility === 'private' && (doc.is_public || sharedCount > 0)) return false;
      if (filters.visibility === 'shared' && (doc.is_public || sharedCount === 0)) return false;
    }
    return true;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const viewParams = getViewQueryParams();

      if (viewMode === 'folder') {
        // Strip the view-context company/group/site params before calling
        // document endpoints. DjangoFilterBackend interprets `company=X` as
        // "Document.company_id == X" which is exact-match on the uploader's
        // company — documents uploaded by a customer belong to the customer,
        // not the viewing supplier, so a supplier looking at MAB saw 0 docs
        // even though the stats endpoint (which doesn't apply filter_queryset)
        // said 880. Scoping is handled by the backend's get_queryset based on
        // the caller's profile and effective company type; we don't need to
        // pass the top-level tenant picker to Documents.
        const { company: _c, group: _g, site: _s, ...documentParams } = viewParams as Record<string, string | number | boolean | undefined>;
        void _c; void _g; void _s;
        const [categoriesResponse, statsResponse] = await Promise.all([
          documentsAPI.getDocumentsByCategory(documentParams),
          documentsAPI.getDocumentStats(documentParams),
        ]);

        let categories = categoriesResponse.data;

        // Filter by search term if provided
        if (searchTerm) {
          categories = categories.map(cat => ({
            ...cat,
            documents: cat.documents.filter(doc =>
              doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
              doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              doc.tags?.toLowerCase().includes(searchTerm.toLowerCase())
            )
          })).filter(cat => cat.documents.length > 0);
        }

        // Apply filters
        const hasActiveFilters = filters.documentType !== 'all' ||
                                  filters.category !== 'all' ||
                                  filters.visibility !== 'all';
        if (hasActiveFilters) {
          categories = categories.map(cat => ({
            ...cat,
            documents: cat.documents.filter(matchesFilters)
          })).filter(cat => cat.documents.length > 0);

          // If filtering by category, only show that category
          if (filters.category !== 'all') {
            const categoryId = parseInt(filters.category, 10);
            categories = categories.filter(cat => cat.id === categoryId);
          }
        }

        setCategoriesWithDocs(categories);
        setStats(statsResponse.data);
      } else {
        // Build API params with filters
        const params: Record<string, string | number | boolean | undefined> = {
          page: pagination.currentPage,
          page_size: pagination.pageSize,
          search: searchTerm || undefined,
          ordering: '-uploaded_at',
        };

        // Add filter params
        if (filters.documentType !== 'all') {
          params['document_type'] = filters.documentType;
        }
        if (filters.category !== 'all') {
          params['category'] = parseInt(filters.category, 10);
        }
        // CR-5327: Forward the three-way visibility state as a single
        // query param so the backend can apply the shared-docs filter
        // (M2M presence check) that can't be expressed via is_public
        // alone. Backend interprets visibility=public/private/shared.
        if (filters.visibility !== 'all') {
          params['visibility'] = filters.visibility;
        }

        // Intentionally NOT forwarding the view-context site/company/group
        // params — see the folder-mode block above. Document suppliership is
        // per-uploader and doesn't align with the top-level tenant picker.
        const [docsResponse, statsResponse] = await Promise.all([
          documentsAPI.getDocuments(params),
          documentsAPI.getDocumentStats({}),
        ]);

        setDocuments(docsResponse.data.results);
        pagination.setFromResponse(docsResponse.data.count);
        setStats(statsResponse.data);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load documents'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    pagination.reset();
  };

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleFilterChange = (key: keyof DocumentFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    pagination.reset();
  };

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    pagination.reset();
  };

  const hasActiveFilters = filters.documentType !== 'all' ||
                           filters.category !== 'all' ||
                           filters.visibility !== 'all';

  const handleDownload = async (doc: Document) => {
    try {
      const response = await documentsAPI.downloadDocument(doc.id);
      window.open(response.data.file_url, '_blank');
    } catch (err: unknown) {
      console.error('Error downloading document:', err);
      toast.error('Failed to download document');
    }
  };

  // Delete document
  const handleDelete = async (doc: Document) => {
    const ok = await confirm({ title: 'Delete Document', description: `Are you sure you want to delete "${doc.title}"?`, confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) return;
    try {
      await documentsAPI.deleteDocument(doc.id);
      toast.success('Document deleted successfully');
      await fetchData();
    } catch (err: unknown) {
      console.error('Error deleting document:', err);
      toast.error(getApiErrorMessage(err, 'Failed to delete document'));
    }
  };

  // Preview document (opens inline in new tab).
  // CR-5331: Use the same HMAC-signed URL flow as Download — the
  // permanent JWT must not ride in the URL (CWE-200: leaks via
  // browser history / Heroku log drains / Referer headers), and the
  // backend's preview endpoint rejects `?token=<jwt>` because it only
  // honours `?u&exp&sig` minted by sign_preview(). The download
  // endpoint already returns the correctly-signed file_url.
  const handlePreview = async (doc: Document) => {
    if (doc.document_type === 'url' && doc.external_url) {
      window.open(doc.external_url, '_blank');
      return;
    }
    try {
      const response = await documentsAPI.downloadDocument(doc.id);
      window.open(response.data.file_url, '_blank');
    } catch (err: unknown) {
      console.error('Error previewing document:', err);
      toast.error('Failed to preview document');
    }
  };

  // CR-5330: Open the details popup for a document. Stash the row so
  // the modal has a stable snapshot even if fetchData() refreshes the
  // list mid-view.
  const handleOpenDetail = (doc: Document) => {
    setDetailDoc(doc);
    detailDialog.open();
  };

  const handleDetailDialogClose = () => {
    detailDialog.close();
    setDetailDoc(null);
  };

  // Edit document
  const handleEdit = (doc: Document) => {
    // CR-5325: Derive the three-way visibility from the doc's flags.
    // is_public=true → public; is_public=false + non-empty share list →
    // shared; otherwise private. The shared list comes from the list
    // serializer's shared_with_company_ids field.
    const sharedIds = doc.shared_with_company_ids ?? [];
    const visibility: DocumentVisibility = doc.is_public
      ? 'public'
      : sharedIds.length > 0
        ? 'shared'
        : 'private';
    setEditForm({
      id: doc.id,
      title: doc.title,
      description: doc.description || '',
      category: doc.category?.toString() || '',
      tags: doc.tags || '',
      version: doc.version || '',
      visibility,
      shared_with_company_ids: sharedIds,
      requires_acceptance: !!doc.requires_acceptance,
    });
    editDialog.open();
  };

  const handleEditDialogClose = () => {
    editDialog.close();
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editForm.title.trim()) {
      toast.error('Please enter a document title');
      return;
    }
    try {
      setEditSaving(true);
      // CR-5325: Send the full upload-shaped payload on edit. JSON keeps
      // shared_with_company_ids as a real array (FormData has no clean
      // way to send an empty list). Visibility maps to:
      //   public  → is_public=true,  shares=[]
      //   shared  → is_public=false, shares=[…]
      //   private → is_public=false, shares=[]
      const payload: Record<string, unknown> = {
        title: editForm.title,
        description: editForm.description,
        tags: editForm.tags,
        version: editForm.version,
        category: editForm.category ? parseInt(editForm.category, 10) : null,
        is_public: editForm.visibility === 'public',
        shared_with_company_ids: editForm.visibility === 'shared'
          ? editForm.shared_with_company_ids
          : [],
        // CR-5329
        requires_acceptance: editForm.requires_acceptance,
      };
      await documentsAPI.updateDocument(editForm.id, payload as Partial<Document>);
      handleEditDialogClose();
      toast.success('Document updated successfully');
      await fetchData();
    } catch (err: unknown) {
      console.error('Error updating document:', err);
      toast.error(getApiErrorMessage(err, 'Failed to update document'));
    } finally {
      setEditSaving(false);
    }
  };

  const handleUploadDialogOpen = () => {
    uploadDialog.open();
  };

  const handleUploadDialogClose = () => {
    uploadDialog.close();
    // Reset form
    setUploadForm(INITIAL_UPLOAD_FORM);
  };

  // Allowed file extensions (must match server-side)
  const ALLOWED_EXTENSIONS = [
    'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
    'txt', 'csv', 'zip', 'rar', '7z',
    'jpg', 'jpeg', 'png', 'gif', 'svg', 'bmp',
  ];

  // Maximum file size (25MB - must match server-side)
  const MAX_FILE_SIZE = 25 * 1024 * 1024;

  const validateFile = (file: File): string | null => {
    // Check file extension
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
      return `Invalid file type. Allowed file types: ${ALLOWED_EXTENSIONS.join(', ')}.`;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds maximum allowed size of ${MAX_FILE_SIZE / 1024 / 1024}MB.`;
    }

    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    // Validate file on client-side
    if (file) {
      const error = validateFile(file);
      if (error) {
        toast.error(error);
        // Clear the file input
        e.target.value = '';
        return;
      }
    }

    setUploadForm({ ...uploadForm, file });
  };

  // CR-5324: Page-level drag-and-drop. Reset the form to upload-file
  // mode with the dropped file pre-selected, then open the dialog so
  // the user can fill in title/folder/visibility before submitting.
  // Validation reuses the same rules as the file picker — invalid drops
  // surface a toast and do not open the dialog.
  const handleDroppedFile = (file: File) => {
    const error = validateFile(file);
    if (error) {
      toast.error(error);
      return;
    }
    setUploadForm({
      ...INITIAL_UPLOAD_FORM,
      file,
      document_type: 'file',
      title: file.name.replace(/\.[^.]+$/, ''),
    });
    uploadDialog.open();
  };

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!uploadForm.title.trim()) {
      toast.error('Please enter a document title');
      return;
    }

    if (uploadForm.document_type === 'file' && !uploadForm.file) {
      toast.error('Please select a file to upload');
      return;
    }

    if (uploadForm.document_type === 'url' && !uploadForm.external_url.trim()) {
      toast.error('Please enter an external URL');
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('document_type', uploadForm.document_type);
      // Translate visibility into backend fields:
      //   public → is_public=True (everyone), no shares
      //   shared → is_public=False + list of customer company ids
      //   private → is_public=False, no shares (owning company only)
      formData.append('is_public', (uploadForm.visibility === 'public').toString());
      if (uploadForm.visibility === 'shared') {
        for (const cid of uploadForm.shared_with_company_ids) {
          formData.append('shared_with_company_ids', String(cid));
        }
      }

      if (uploadForm.category) {
        formData.append('category', uploadForm.category);
      }

      if (uploadForm.tags) {
        formData.append('tags', uploadForm.tags);
      }

      if (uploadForm.version) {
        formData.append('version', uploadForm.version);
      }

      // CR-5329
      formData.append('requires_acceptance', uploadForm.requires_acceptance.toString());

      if (uploadForm.document_type === 'file' && uploadForm.file) {
        formData.append('file', uploadForm.file);
      } else if (uploadForm.document_type === 'url') {
        formData.append('external_url', uploadForm.external_url);
      }

      await documentsAPI.createDocument(formData);

      toast.success('Document uploaded successfully');
      await fetchData(); // Refresh the document list first
      handleUploadDialogClose();
    } catch (err: unknown) {
      console.error('Error uploading document:', err);
      toast.error(getApiErrorMessage(err, 'Failed to upload document'));
    } finally {
      setUploading(false);
    }
  };

  return {
    // View state
    viewMode,
    setViewMode,

    // Loading states
    loading,
    error,
    uploading,

    // Search
    searchTerm,
    handleSearchChange,

    // Filters
    showFilters,
    filters,
    hasActiveFilters,
    handleToggleFilters,
    handleFilterChange,
    handleClearFilters,

    // Data
    stats,
    categoriesWithDocs,
    documents,
    categories,
    pagination,

    // Upload dialog
    uploadDialog,
    uploadForm,
    setUploadForm,
    handleUploadDialogOpen,
    handleUploadDialogClose,
    handleFileChange,
    handleDroppedFile,
    handleUploadSubmit,

    // Actions
    fetchData,
    handleDownload,
    handleDelete,
    handleEdit,
    handlePreview,

    // Edit dialog
    editDialog,
    editForm,
    setEditForm,
    handleEditDialogClose,
    handleEditSubmit,
    editSaving,

    // CR-5330: Detail dialog
    detailDialog,
    detailDoc,
    handleOpenDetail,
    handleDetailDialogClose,
  };
}
